import './polyfills';

// This is imported first to enable us to catch as many exceptions as possible
import './sentry';

// This assignment to __webpack_nonce__ needs to happen before other imports
// to ensure that dynamically loaded modules use the CSP nonce
import './csp';

// This assignment to __webpack_public_path__ needs to happen before all other
// imports in order to have correct asset URL resolution
// see: https://webpack.js.org/guides/public-path/#on-the-fly
import './public-path';

import React from 'react';

import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import Loadable from 'react-loadable';

import { appWasServerSideRendered } from 'src/utils/ssr';

import App from './app/index';
import { initializeBrowserMetrics } from './components/browser-metrics';
import { initUFO } from './components/react-ufo/initUFO';

function setupWdyr() {
  const qs = require('qs');
  const { wdyu, wdyr } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  // We previously used the "why-did-you-update" library, but that was
  // deprecated by its author in favour of "why-did-you-render". Keeping
  // the old "wdyu" support around in case people are used to using that.
  const wdyrTarget = wdyr || wdyu;

  if (wdyrTarget !== undefined) {
    // eslint-disable-next-line import/no-extraneous-dependencies
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    const ReactRedux = require('react-redux');
    whyDidYouRender(React, {
      include: [new RegExp(wdyrTarget)],
      trackExtraHooks: [[ReactRedux, 'useSelector']],
    });
  }
}

function setupA11y() {
  const qs = require('qs');
  const { a11y } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  if (a11y !== undefined) {
    // Show accessibility errors in development
    // https://github.com/dequelabs/axe-core-npm/tree/develop/packages/react
    // * Uses advanced console logging features, works best in Chrome
    //   (limited functionality in Safari and Firefox)
    //
    // eslint-disable-next-line import/no-extraneous-dependencies
    const axe = require('@axe-core/react');
    axe(React, ReactDOM, 1000);
  }
}

function setupReactWarnings() {
  // Ignore some warnings from React 18 about future deprecations
  // eslint-disable-next-line no-console
  const originalError = console.error;
  const warningSources = [
    'IntlProvider',
    /InjectIntl/,
    'ConnectProvider',
    'AnalyticsListener',
    'EngagementProvider',
    'InjectIntl(KeyboardShortcutDialog)',
    'AnalyticsContext',
  ];
  // eslint-disable-next-line no-console
  console.error = function filterWarnings(msg, ...args) {
    if (
      typeof msg === 'string' &&
      (msg.includes('contextTypes') || msg.includes('childContextTypes'))
    ) {
      if (
        warningSources.some(source =>
          args.some(arg => new RegExp(source).test(arg))
        )
      ) {
        return;
      }
    }
    originalError(msg, ...args);
  };
}

if (process.env.NODE_ENV === 'development') {
  setupWdyr();
  setupA11y();
  setupReactWarnings();
}

// This is the one place we should reference app data from the window
// eslint-disable-next-line no-restricted-properties
const appData = window.__app_data__;

// Start up Browser Metrics
initializeBrowserMetrics({
  isAtlassian: appData.user?.isAtlassian || false,
  frontbucket_version: appData.frontbucket_version || 'local',
  tenantId: appData.tenantId,
});

initUFO(appData);

const rootEl: HTMLElement | null | undefined = document.getElementById('root');
if (rootEl) {
  const root = createRoot(rootEl);
  if (appWasServerSideRendered()) {
    Loadable.preloadReady().then(() => {
      root.render(<App appData={appData} />);
    });
  } else {
    root.render(<App appData={appData} />);
  }
}
